"use client";

import { ChevronLeft, ChevronRight, ShoppingCart, SlidersHorizontal, Sparkles } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useSession } from "next-auth/react";
import { useCallback, useEffect, useRef, useState } from "react";

import { heroSlides as defaultHeroSlides } from "@/lib/data";
import type { Promotion } from "@/types";

export function HeroCarousel({ slides }: { slides?: Promotion[] }) {
  const { data: session } = useSession();
  const isAdmin = session?.user?.role === "ADMIN" || session?.user?.role === "ADMIN1";

  const activeSlides =
    slides && slides.length > 0
      ? slides
      : defaultHeroSlides.map((s, idx) => ({
          id: `fallback-${idx}`,
          eyebrow: s.eyebrow,
          title: s.title,
          description: s.description,
          image: s.image,
          images: [s.image],
          cta: s.cta,
          link: s.link || "/search",
          isActive: true,
          sortOrder: idx,
        }));

  // Display only active slides without artificial repetition
  const displayCards = activeSlides;

  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  // Drag-to-scroll & swipe state
  const isPointerDownRef = useRef(false);
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const scrollLeftStartRef = useRef(0);
  const lastXRef = useRef(0);
  const lastTimeRef = useRef(0);
  const velocityRef = useRef(0);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 10);
  }, []);

  useEffect(() => {
    checkScroll();
    window.addEventListener("resize", checkScroll);
    return () => window.removeEventListener("resize", checkScroll);
  }, [checkScroll]);

  // Global pointer listeners ensure smooth drag even if cursor/touch leaves the container
  useEffect(() => {
    const handleGlobalPointerMove = (e: PointerEvent) => {
      if (!isPointerDownRef.current) return;
      const el = scrollRef.current;
      if (!el) return;

      const currentX = e.clientX;
      const deltaX = currentX - startXRef.current;

      if (!isDraggingRef.current && Math.abs(deltaX) > 4) {
        isDraggingRef.current = true;
      }

      if (isDraggingRef.current) {
        const now = performance.now();
        const dt = now - lastTimeRef.current;
        if (dt > 10) {
          velocityRef.current = (currentX - lastXRef.current) / dt;
          lastXRef.current = currentX;
          lastTimeRef.current = now;
        }
        el.scrollLeft = scrollLeftStartRef.current - deltaX;
      }
    };

    const handleGlobalPointerUp = () => {
      if (!isPointerDownRef.current) return;
      isPointerDownRef.current = false;

      const el = scrollRef.current;
      if (isDraggingRef.current && el) {
        const v = velocityRef.current;
        if (Math.abs(v) > 0.25) {
          el.scrollBy({ left: -v * 220, behavior: "smooth" });
        }
        // Delay resetting isDraggingRef so that the click event on any card Link is suppressed
        setTimeout(() => {
          isDraggingRef.current = false;
        }, 120);
      }
    };

    window.addEventListener("pointermove", handleGlobalPointerMove, { passive: true });
    window.addEventListener("pointerup", handleGlobalPointerUp);
    window.addEventListener("pointercancel", handleGlobalPointerUp);

    return () => {
      window.removeEventListener("pointermove", handleGlobalPointerMove);
      window.removeEventListener("pointerup", handleGlobalPointerUp);
      window.removeEventListener("pointercancel", handleGlobalPointerUp);
    };
  }, []);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0 && e.pointerType === "mouse") return;
    const el = scrollRef.current;
    if (!el) return;

    isPointerDownRef.current = true;
    isDraggingRef.current = false;
    startXRef.current = e.clientX;
    lastXRef.current = e.clientX;
    lastTimeRef.current = performance.now();
    velocityRef.current = 0;
    scrollLeftStartRef.current = el.scrollLeft;
  };

  function scroll(direction: "left" | "right") {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = 230;
    const distance = direction === "left" ? -cardWidth * 2 : cardWidth * 2;
    el.scrollBy({ left: distance, behavior: "smooth" });
  }

  return (
    <section className="relative w-full overflow-hidden bg-gradient-to-b from-[#F5F7FA] to-[#EDF2F7] py-3 sm:py-5 border-b border-slate-200/80 dark:from-slate-950 dark:to-slate-900 dark:border-white/10">
      <div className="relative mx-auto max-w-[1500px] px-3 sm:px-4">
        {/* Official Sticker Promotional Ribbon Hero Callout */}
        <div className="mb-3.5 sm:mb-5 overflow-hidden rounded-2xl bg-gradient-to-r from-[#073B70] via-[#087DB5] to-[#073B70] p-4 sm:p-6 text-white shadow-md relative border border-white/10">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="grid h-12 w-12 sm:h-14 sm:w-14 shrink-0 place-items-center rounded-2xl bg-[#FF8A00] text-white shadow-md">
                <ShoppingCart className="h-6 w-6 sm:h-7 sm:w-7" />
              </div>
              <div>
                <div className="inline-flex items-center gap-1.5 rounded-full bg-white/20 px-2.5 py-0.5 text-[10px] sm:text-xs font-bold uppercase tracking-wider text-teal-200">
                  <Sparkles className="h-3 w-3 text-[#00B7B0]" />
                  <span>MOVA MARKET ETH</span>
                </div>
                <h1 className="mt-1 text-lg sm:text-2xl md:text-3xl font-bold tracking-tight text-white">
                  YOUR NEXT FAVORITE FIND <span className="text-[#FF8A00]">IS ONE CLICK AWAY!</span>
                </h1>
                <p className="text-xs sm:text-sm font-medium text-slate-200">
                  We Don&apos;t Just Sell, We Guarantee Quality! &bull; Discover verified products delivered across Ethiopia.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2.5 shrink-0 w-full sm:w-auto">
              <Link
                href="/search"
                className="flex-1 sm:flex-initial text-center rounded-xl bg-[#FF8A00] px-5 py-2.5 text-xs sm:text-sm font-bold text-white shadow-sm transition hover:bg-[#E67C00] active:scale-95"
              >
                Shop Now
              </Link>
              <Link
                href="/search?sort=discount"
                className="flex-1 sm:flex-initial text-center rounded-xl border border-white/30 bg-white/10 px-4 py-2.5 text-xs sm:text-sm font-bold text-white transition hover:bg-white/20"
              >
                Exclusive Deals
              </Link>
            </div>
          </div>
        </div>

        {isAdmin && (
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">
              Storefront Hero Banner Carousel
            </span>
            <Link
              href="/admin/promotions"
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white/95 px-2.5 py-1 text-[11px] font-bold text-slate-800 shadow-xs transition hover:border-[#087DB5] hover:bg-white hover:text-[#087DB5] dark:border-white/10 dark:bg-slate-900/90 dark:text-slate-200"
              title="Configure promotion cards in Admin"
            >
              <SlidersHorizontal className="h-3 w-3 text-[#087DB5]" />
              <span>Manage Banners (Admin)</span>
            </Link>
          </div>
        )}

        {/* Horizontal Card Scroll Track */}
        <div
          ref={scrollRef}
          onScroll={checkScroll}
          onPointerDown={handlePointerDown}
          className="flex gap-3 overflow-x-auto py-1 no-scrollbar sm:gap-3.5 select-none cursor-grab active:cursor-grabbing overscroll-x-contain touch-auto"
        >
          {displayCards.map((slide, index) => (
            <HeroSlideCard
              key={`${slide.id}-${index}`}
              slide={slide}
              index={index}
              getIsDragging={() => isDraggingRef.current}
            />
          ))}
        </div>

        {/* Left Arrow Button */}
        {canScrollLeft && (
          <button
            type="button"
            onClick={() => scroll("left")}
            className="absolute left-1 top-1/2 -translate-y-1/2 z-20 hidden sm:flex h-16 w-8 items-center justify-center rounded-r-md border border-l-0 border-slate-300/80 bg-white/95 text-slate-800 shadow-md transition hover:bg-white hover:text-black dark:border-white/10 dark:bg-slate-900/95 dark:text-white"
            aria-label="Previous promotional items"
          >
            <ChevronLeft className="h-5 w-5 stroke-[2.5]" />
          </button>
        )}

        {/* Right Arrow Button (matches user screenshot exactly) */}
        {canScrollRight && (
          <button
            type="button"
            onClick={() => scroll("right")}
            className="absolute right-1 top-1/2 -translate-y-1/2 z-20 hidden sm:flex h-16 w-8 items-center justify-center rounded-l-md border border-r-0 border-slate-300/80 bg-white/95 text-slate-800 shadow-md transition hover:bg-white hover:text-black dark:border-white/10 dark:bg-slate-900/95 dark:text-white"
            aria-label="Next promotional items"
          >
            <ChevronRight className="h-5 w-5 stroke-[2.5]" />
          </button>
        )}
      </div>
    </section>
  );
}

function HeroSlideCard({
  slide,
  index,
  getIsDragging,
}: {
  slide: Promotion;
  index: number;
  getIsDragging?: () => boolean;
}) {
  const candidateImages = slide.images && slide.images.length > 0 ? slide.images : [slide.image];
  const images = Array.from(new Set(candidateImages.filter(Boolean)));
  const hasMultiple = images.length > 1;

  const [activeImageIdx, setActiveImageIdx] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  // Auto-slide every 3.5s when not hovered
  useEffect(() => {
    if (!hasMultiple || isHovered) return;
    const interval = setInterval(() => {
      setActiveImageIdx((prev) => (prev + 1) % images.length);
    }, 3500 + (index % 3) * 500);

    return () => clearInterval(interval);
  }, [hasMultiple, isHovered, images.length, index]);

  const hasPreformattedImage = slide.image.startsWith("/promotions/");

  const handlePrev = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveImageIdx((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleNext = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveImageIdx((prev) => (prev + 1) % images.length);
  };

  const handleDotClick = (e: React.MouseEvent, dotIdx: number) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveImageIdx(dotIdx);
  };

  const targetLink =
    slide.link && slide.link !== "/search"
      ? slide.link
      : slide.targetType === "collection" && slide.target
      ? `/search?collection=${encodeURIComponent(slide.target)}`
      : slide.targetType === "category" && slide.target
      ? `/search?category=${encodeURIComponent(slide.target)}`
      : slide.link || "/search";

  return (
    <Link
      href={targetLink}
      draggable={false}
      onDragStart={(e) => e.preventDefault()}
      onClick={(e) => {
        if (getIsDragging?.()) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="group relative w-[155px] sm:w-[185px] md:w-[215px] shrink-0 select-none overflow-hidden rounded-[14px] sm:rounded-[18px] bg-white shadow-xs transition-all duration-300 hover:-translate-y-1 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-amazon-orange dark:bg-slate-900"
      style={{ aspectRatio: "196 / 316" }}
    >
      <div className="relative h-full w-full overflow-hidden select-none">
        {/* Sliding images track */}
        <div
          className="flex h-full w-full pointer-events-none select-none transition-transform duration-500 ease-out will-change-transform"
          style={{ transform: `translateX(-${activeImageIdx * 100}%)` }}
        >
          {images.map((imgSrc, imgIdx) => (
            <div key={`${slide.id}-img-${imgIdx}`} className="relative h-full w-full shrink-0 select-none">
              <Image
                src={imgSrc}
                alt={`${slide.title} ${imgIdx > 0 ? `(${imgIdx + 1})` : ""}`}
                fill
                draggable={false}
                sizes="(max-width: 640px) 155px, (max-width: 768px) 185px, 215px"
                className="object-cover pointer-events-none select-none"
                priority={index < 4 && imgIdx === 0}
              />
            </div>
          ))}
        </div>

        {/* Top Indicators / Story Bars for sub-images */}
        {hasMultiple && (
          <div className="absolute top-2 inset-x-2 z-20 flex items-center justify-center gap-1 pointer-events-auto">
            {images.map((_, dotIdx) => (
              <button
                key={dotIdx}
                type="button"
                onClick={(e) => handleDotClick(e, dotIdx)}
                onMouseEnter={() => setActiveImageIdx(dotIdx)}
                className={`h-1 sm:h-1.5 rounded-full transition-all duration-300 ${
                  activeImageIdx === dotIdx
                    ? "w-4 sm:w-5 bg-amazon-orange shadow-xs ring-1 ring-black/20"
                    : "w-1.5 bg-white/60 hover:bg-white shadow-xs"
                }`}
                aria-label={`Show image ${dotIdx + 1} of ${images.length}`}
              />
            ))}
          </div>
        )}

        {/* Hover Prev/Next Arrows for sub-images */}
        {hasMultiple && (
          <>
            <button
              type="button"
              onClick={handlePrev}
              className="absolute left-1.5 top-1/2 -translate-y-1/2 z-20 flex h-6 w-6 sm:h-7 sm:w-7 items-center justify-center rounded-full bg-black/60 text-white shadow-md backdrop-blur-xs opacity-0 transition-all duration-200 group-hover:opacity-100 hover:bg-black/85 hover:scale-105 pointer-events-auto focus:opacity-100"
              aria-label="Previous sub image"
            >
              <ChevronLeft className="h-3.5 w-3.5 sm:h-4 sm:w-4 stroke-[2.5]" />
            </button>
            <button
              type="button"
              onClick={handleNext}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 z-20 flex h-6 w-6 sm:h-7 sm:w-7 items-center justify-center rounded-full bg-black/60 text-white shadow-md backdrop-blur-xs opacity-0 transition-all duration-200 group-hover:opacity-100 hover:bg-black/85 hover:scale-105 pointer-events-auto focus:opacity-100"
              aria-label="Next sub image"
            >
              <ChevronRight className="h-3.5 w-3.5 sm:h-4 sm:w-4 stroke-[2.5]" />
            </button>
          </>
        )}

        {/* Overlay text for products or custom banners without baked-in text */}
        {!hasPreformattedImage && (
          <div
            className={`pointer-events-none absolute inset-0 flex flex-col justify-between p-2.5 sm:p-3.5 bg-gradient-to-t from-black/85 via-black/35 to-black/60 text-white ${
              hasMultiple ? "pt-4 sm:pt-5" : ""
            }`}
          >
            <div>
              {slide.eyebrow && (
                <span className="inline-block rounded-md bg-[#FF8A00] px-1.5 sm:px-2 py-0.5 text-[9px] sm:text-[10px] font-bold uppercase tracking-wider text-white shadow-xs">
                  {slide.eyebrow}
                </span>
              )}
              <h3 className="mt-1 sm:mt-1.5 line-clamp-2 text-xs sm:text-sm font-bold leading-snug tracking-tight text-white drop-shadow-md">
                {slide.title}
              </h3>
            </div>
            <div className="space-y-1 sm:space-y-1.5">
              {slide.description && (
                <p className="line-clamp-1 text-[10px] sm:text-[11px] font-semibold text-[#00B7B0]">
                  {slide.description}
                </p>
              )}
              {slide.cta && (
                <span className="inline-flex items-center rounded-full bg-[#087DB5] px-2.5 sm:px-3 py-0.5 sm:py-1 text-[10px] sm:text-[11px] font-bold text-white shadow-sm transition group-hover:bg-[#073B70]">
                  {slide.cta}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </Link>
  );
}
